import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  Search, 
  Shield, 
  ShieldCheck,
  User,
  Building2,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  Star,
  Pencil,
  Check,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  UserPlus,
  ArrowUpCircle,
  UserMinus,
  Link2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_STYLES = {
  client: { bg: 'bg-gray-600/20', text: 'text-gray-400', label: 'Client' },
  partner: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Partner' },
  admin: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Admin' },
  super_admin: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Super Admin' },
};

// ─── Secondary Users Panel (expandable) ─────────────────────────────
const SecondaryUsersPanel = ({ userId, userName, token, onRefreshParent }) => {
  const [secondaryUsers, setSecondaryUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  // Searchable dropdown state
  const [allUsers, setAllUsers] = useState([]);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Inline name editing state
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const nameEditRef = useRef(null);

  const fetchSecondary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/secondary-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecondaryUsers(res.data || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load secondary users'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load users for dropdown', err);
    }
  };

  useEffect(() => { fetchSecondary(); fetchAllUsers(); }, [userId]);

  useEffect(() => {
    if (editingNameId && nameEditRef.current) {
      nameEditRef.current.focus();
      nameEditRef.current.select();
    }
  }, [editingNameId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  // Filter eligible users for dropdown
  const secondaryIds = new Set(secondaryUsers.map(s => s.user_id));
  const eligibleUsers = allUsers.filter(u => {
    if (u.id === userId) return false; // exclude current primary
    if (u.primary_user_id) return false; // already linked to someone
    if (secondaryIds.has(u.id)) return false; // already in this list
    return true;
  });

  const filteredDropdown = eligibleUsers.filter(u => {
    if (!dropdownSearch) return true;
    const q = dropdownSearch.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const handleAdd = async () => {
    if (!selectedUser) return;
    setAdding(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/secondary-users`,
        { email: selectedUser.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`${res.data.full_name || res.data.email} has been added as a secondary user`);
      setSelectedUser(null);
      setDropdownSearch('');
      fetchSecondary();
      fetchAllUsers();
      onRefreshParent();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to add secondary user'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (secId) => {
    setActionId(secId);
    setError('');
    try {
      await axios.delete(`${API}/admin/users/${userId}/secondary-users/${secId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSecondaryUsers(prev => prev.filter(u => u.user_id !== secId));
      showSuccess('Access removed');
      setConfirmAction(null);
      fetchAllUsers();
      onRefreshParent();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to remove access'));
    } finally {
      setActionId(null);
    }
  };

  const handlePromote = async (secId) => {
    setActionId(secId);
    setError('');
    try {
      await axios.post(`${API}/admin/users/${secId}/promote-to-primary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Successfully promoted to primary user');
      setConfirmAction(null);
      onRefreshParent();
      fetchSecondary();
      fetchAllUsers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to promote user'));
    } finally {
      setActionId(null);
    }
  };

  const handleNameEdit = async (secUserId) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    const oldName = secondaryUsers.find(s => s.user_id === secUserId)?.full_name;
    // Optimistic update
    setSecondaryUsers(prev => prev.map(s => s.user_id === secUserId ? { ...s, full_name: trimmed } : s));
    setEditingNameId(null);
    setEditingNameValue('');
    try {
      await axios.put(`${API}/admin/users/${secUserId}/name`,
        { full_name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Name updated');
      onRefreshParent();
    } catch (err) {
      // Revert
      setSecondaryUsers(prev => prev.map(s => s.user_id === secUserId ? { ...s, full_name: oldName } : s));
      setError(extractErrorMessage(err, 'Failed to update name'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-[#0D1117] border border-gray-800 rounded-lg p-5 mt-1" data-testid="secondary-users-panel">
      <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
        <Link2 className="w-4 h-4 text-[#00E87A]" />
        Secondary Users
      </h3>

      {/* Success / Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-400 text-sm" data-testid="secondary-success">
          <CheckCircle className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm" data-testid="secondary-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300 text-xs">Dismiss</button>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmAction && (
        <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg" data-testid="confirm-dialog">
          <p className="text-white text-sm mb-1 font-medium">{confirmAction.title}</p>
          <p className="text-gray-400 text-sm mb-3 whitespace-pre-line">{confirmAction.message}</p>
          <div className="flex gap-2">
            <button
              onClick={confirmAction.onConfirm}
              disabled={actionId !== null}
              className={`px-3 py-1.5 rounded text-sm font-medium text-white flex items-center gap-1.5 ${confirmAction.type === 'promote' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'} disabled:opacity-50`}
              data-testid="confirm-action-btn"
            >
              {actionId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {confirmAction.confirmLabel}
            </button>
            <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 rounded text-sm text-gray-400 bg-gray-800 hover:bg-gray-700" data-testid="cancel-action-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Secondary Users Table */}
      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
      ) : secondaryUsers.length === 0 ? (
        <p className="text-gray-500 text-sm py-4" data-testid="no-secondary-users">No secondary users added yet</p>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full" data-testid="secondary-users-table">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Name</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Email</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2 hidden sm:table-cell">Date Added</th>
                <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {secondaryUsers.map(sec => {
                const isEditing = editingNameId === sec.user_id;
                return (
                  <tr key={sec.user_id} className="hover:bg-gray-800/20" data-testid={`secondary-row-${sec.user_id}`}>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            ref={nameEditRef}
                            type="text"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleNameEdit(sec.user_id);
                              if (e.key === 'Escape') { setEditingNameId(null); setEditingNameValue(''); }
                            }}
                            className="px-2 py-0.5 bg-[#161B22] border border-[#00E87A] rounded text-white text-sm w-40 focus:outline-none"
                            maxLength={100}
                            data-testid={`edit-name-input-${sec.user_id}`}
                          />
                          <button
                            onMouseDown={(e) => { e.preventDefault(); handleNameEdit(sec.user_id); }}
                            className="text-[#00E87A] hover:text-green-300 p-0.5"
                            data-testid={`edit-name-save-${sec.user_id}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onMouseDown={(e) => { e.preventDefault(); setEditingNameId(null); setEditingNameValue(''); }}
                            className="text-gray-500 hover:text-gray-300 p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group">
                          <span className="text-white text-sm">{sec.full_name || <span className="text-gray-600 italic">No name</span>}</span>
                          <button
                            onClick={() => { setEditingNameId(sec.user_id); setEditingNameValue(sec.full_name || ''); }}
                            className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-[#00E87A] transition-opacity p-0.5"
                            data-testid={`edit-name-btn-${sec.user_id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-400 text-sm">{sec.email}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-sm hidden sm:table-cell">{formatDate(sec.created_at)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setConfirmAction({
                            type: 'promote',
                            title: `Promote ${sec.full_name || sec.email} to primary user?`,
                            message: `This will:\n• Transfer all companies to ${sec.full_name || sec.email}\n• ${userName} will become a secondary user\n\nThis action cannot be undone.`,
                            confirmLabel: 'Confirm Promote',
                            onConfirm: () => handlePromote(sec.user_id)
                          })}
                          className="px-2.5 py-1 rounded text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 flex items-center gap-1 transition-colors"
                          data-testid={`promote-btn-${sec.user_id}`}
                        >
                          <ArrowUpCircle className="w-3.5 h-3.5" /> Promote
                        </button>
                        <button
                          onClick={() => setConfirmAction({
                            type: 'remove',
                            title: `Remove ${sec.full_name || sec.email}'s access?`,
                            message: `They will no longer be able to view ${userName}'s companies.`,
                            confirmLabel: 'Remove Access',
                            onConfirm: () => handleRemove(sec.user_id)
                          })}
                          className="px-2.5 py-1 rounded text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 flex items-center gap-1 transition-colors"
                          data-testid={`remove-btn-${sec.user_id}`}
                        >
                          <UserMinus className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Searchable Dropdown Add Form */}
      <div className="pt-3 border-t border-gray-800" data-testid="add-secondary-form">
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Add Secondary User</p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1" ref={dropdownRef}>
            <div className="flex items-center">
              <Search className="absolute left-3 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder={selectedUser ? `${selectedUser.full_name || ''} — ${selectedUser.email}` : "Search users by name or email..."}
                value={dropdownSearch}
                onChange={(e) => { setDropdownSearch(e.target.value); setSelectedUser(null); setDropdownOpen(true); }}
                onFocus={() => setDropdownOpen(true)}
                className={`w-full pl-9 pr-3 py-2 bg-[#161B22] border border-gray-700 rounded text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A] ${selectedUser ? 'text-[#00E87A]' : 'text-white'}`}
                data-testid="add-secondary-search"
              />
            </div>
            {dropdownOpen && dropdownSearch !== '' && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#161B22] border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="user-dropdown">
                {filteredDropdown.length === 0 ? (
                  <div className="px-3 py-2.5 text-gray-500 text-sm">No eligible users found</div>
                ) : (
                  filteredDropdown.slice(0, 20).map(u => (
                    <button
                      key={u.id}
                      onClick={() => { setSelectedUser(u); setDropdownSearch(`${u.full_name || ''} — ${u.email}`); setDropdownOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors flex items-center gap-2"
                      data-testid={`dropdown-option-${u.id}`}
                    >
                      <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate">{u.full_name || <span className="text-gray-600 italic">No name</span>}</p>
                        <p className="text-gray-500 text-xs truncate">{u.email}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !selectedUser}
            className="px-4 py-2 bg-[#00E87A] text-black rounded text-sm font-medium hover:bg-[#00d46e] disabled:opacity-50 flex items-center gap-1.5 flex-shrink-0"
            data-testid="add-secondary-btn"
          >
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Main Component ──────────────────────────────────────────────────
const SuperAdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    if (editingNameId && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingNameId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(extractErrorMessage(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser?.id && newRole !== 'super_admin') {
      setError('You cannot demote your own account');
      return;
    }
    
    setUpdatingId(userId);
    setError('');
    try {
      await axios.put(`${API}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Role updated successfully');
      fetchUsers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update role'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTogglePartner = async (userId, currentPartnerStatus) => {
    const newStatus = !currentPartnerStatus;
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_partner: newStatus } : u));
    setUpdatingId(userId);
    setError('');
    try {
      await axios.put(`${API}/admin/users/${userId}/partner`,
        { is_partner: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Partner status ${newStatus ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_partner: currentPartnerStatus } : u));
      setError(extractErrorMessage(err, 'Failed to update partner status'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDiscountChange = async (userId, newPercentage) => {
    const pct = parseInt(newPercentage, 10);
    const oldPct = users.find(u => u.id === userId)?.discount_percentage ?? 0;
    if (pct === oldPct) return;

    setUsers(prev => prev.map(u => u.id === userId ? { ...u, discount_percentage: pct } : u));
    setUpdatingId(userId);
    setError('');
    try {
      await axios.patch(`${API}/admin/users/${userId}/discount`,
        { discount_percentage: pct },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Discount updated to ${pct}%`);
    } catch (err) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, discount_percentage: oldPct } : u));
      setError(extractErrorMessage(err, 'Failed to update discount'));
    } finally {
      setUpdatingId(null);
    }
  };

  const startEditingName = (user) => {
    setEditingNameId(user.id);
    setEditingNameValue(user.full_name || '');
  };

  const cancelEditingName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const handleNameSave = async (userId) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) {
      setError('Name cannot be empty');
      return;
    }

    const oldName = users.find(u => u.id === userId)?.full_name;
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, full_name: trimmed } : u));
    setEditingNameId(null);
    setEditingNameValue('');
    setError('');

    try {
      await axios.put(`${API}/admin/users/${userId}/name`,
        { full_name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Name updated');
    } catch (err) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, full_name: oldName } : u));
      setError(extractErrorMessage(err, 'Failed to update name'));
    }
  };

  const handleNameKeyDown = (e, userId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameSave(userId);
    } else if (e.key === 'Escape') {
      cancelEditingName();
    }
  };

  const handleDeleteUser = async (userId, email) => {
    setUpdatingId(userId);
    setError('');
    try {
      // Check if user has secondary users linked
      const secRes = await axios.get(`${API}/admin/users/${userId}/secondary-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const secCount = (secRes.data || []).length;
      if (secCount > 0) {
        setError(`Cannot delete this user. They have ${secCount} secondary user(s) linked to their account. Remove all secondary users first, then try again.`);
        setUpdatingId(null);
        return;
      }
    } catch (err) {
      // If the check fails, proceed with normal confirm (non-blocking)
    }

    if (!window.confirm(`Are you sure you want to delete ${email}? This action can be reversed by a database admin.`)) {
      setUpdatingId(null);
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess(`User ${email} deleted`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete user'));
    } finally {
      setUpdatingId(null);
    }
  };

  // Compute secondary user counts and primary info for badge/panel display
  const secondaryCountMap = {};
  const primaryInfoMap = {}; // { userId: { name, email, id } }
  users.forEach(u => {
    if (u.primary_user_id) {
      secondaryCountMap[u.primary_user_id] = (secondaryCountMap[u.primary_user_id] || 0) + 1;
      const primary = users.find(p => p.id === u.primary_user_id);
      if (primary) {
        primaryInfoMap[u.id] = { name: primary.full_name || primary.email, email: primary.email, id: primary.id };
      }
    }
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-gray-400 text-sm mt-1">
            {filteredUsers.length === users.length
              ? `${users.length} total users`
              : `${filteredUsers.length} of ${users.length} users`}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div data-testid="error-message" className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* Search & Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
            data-testid="search-input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A] text-sm"
          data-testid="role-filter"
        >
          <option value="all">All Roles</option>
          {Object.entries(ROLE_STYLES).map(([key, style]) => (
            <option key={key} value={key}>{style.label}</option>
          ))}
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">User</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Role</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Joined</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Last Login</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Companies</th>
                  <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredUsers.map((user) => {
                  const roleStyle = ROLE_STYLES[user.role] || ROLE_STYLES.client;
                  const isCurrentUser = user.id === currentUser?.id;
                  const isEditingName = editingNameId === user.id;
                  const isExpanded = expandedUserId === user.id;
                  const isSecondary = !!user.primary_user_id;
                  const secondaryCount = secondaryCountMap[user.id] || 0;
                  const isPrimaryWithSec = !isSecondary && secondaryCount > 0;
                  
                  return (
                    <React.Fragment key={user.id}>
                      <tr
                        className={`hover:bg-gray-800/30 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-800/20' : ''}`}
                        data-testid={`user-row-${user.id}`}
                        onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                              {isEditingName ? (
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    ref={nameInputRef}
                                    type="text"
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    onKeyDown={(e) => handleNameKeyDown(e, user.id)}
                                    onBlur={() => {
                                      if (editingNameValue.trim()) {
                                        handleNameSave(user.id);
                                      } else {
                                        cancelEditingName();
                                      }
                                    }}
                                    className="px-2 py-0.5 bg-[#0D1117] border border-[#00E87A] rounded text-white text-sm font-medium w-44 focus:outline-none"
                                    maxLength={100}
                                    data-testid={`name-input-${user.id}`}
                                  />
                                  <button
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleNameSave(user.id); }}
                                    className="text-[#00E87A] hover:text-green-300 p-0.5"
                                    data-testid={`name-save-${user.id}`}
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); cancelEditingName(); }}
                                    className="text-gray-500 hover:text-gray-300 p-0.5"
                                    data-testid={`name-cancel-${user.id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 group">
                                  <p className="text-white text-sm font-medium truncate" data-testid={`user-name-${user.id}`}>
                                    {user.full_name || <span className="text-gray-600 italic">No name</span>}
                                    {isCurrentUser && (
                                      <span className="ml-1.5 text-xs text-gray-500">(You)</span>
                                    )}
                                  </p>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); startEditingName(user); }}
                                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-[#00E87A] transition-opacity p-0.5"
                                    data-testid={`name-edit-${user.id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <p className="text-gray-500 text-xs truncate" data-testid={`user-email-${user.id}`}>
                                  {user.email}
                                </p>
                                {/* Badges */}
                                {isPrimaryWithSec && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#00E87A]/15 text-[#00E87A]" data-testid={`badge-primary-${user.id}`}>
                                    <Link2 className="w-2.5 h-2.5" /> Primary ({secondaryCount})
                                  </span>
                                )}
                                {isSecondary && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/15 text-orange-400" data-testid={`badge-secondary-${user.id}`} title={primaryInfoMap[user.id] ? `Linked to: ${primaryInfoMap[user.id].name}` : ''}>
                                    Secondary
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${roleStyle.bg} ${roleStyle.text}`}>
                            {user.role === 'super_admin' ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                            {roleStyle.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-gray-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(user.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="text-gray-400 text-sm">
                            {formatDate(user.last_login) || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <div className="flex items-center gap-1 text-gray-400 text-sm">
                            <Building2 className="w-4 h-4" />
                            {user.companies_count || 0}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            {/* Expand Toggle */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpandedUserId(isExpanded ? null : user.id); }}
                              className="px-2 py-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                              title="Secondary Users"
                              data-testid={`expand-toggle-${user.id}`}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>

                            {/* Role Dropdown */}
                            <select
                              value={user.role || 'client'}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              disabled={updatingId === user.id || isCurrentUser}
                              className={`px-3 py-1.5 bg-[#0D1117] border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-[#00E87A] ${
                                isCurrentUser ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              data-testid={`role-select-${user.id}`}
                            >
                              <option value="client">Client</option>
                              <option value="partner">Partner</option>
                              <option value="admin">Admin</option>
                              <option value="super_admin">Super Admin</option>
                            </select>
                            
                            {/* Partner Toggle */}
                            <button
                              onClick={() => handleTogglePartner(user.id, user.is_partner)}
                              disabled={updatingId === user.id}
                              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                user.is_partner
                                  ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                              data-testid={`partner-toggle-${user.id}`}
                            >
                              {updatingId === user.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Star className={`w-4 h-4 ${user.is_partner ? 'fill-current' : ''}`} />
                              )}
                            </button>

                            {/* Partner Discount — super_admin only, primary users only */}
                            {currentUser?.role === 'super_admin' && !isSecondary && (
                              <select
                                value={user.discount_percentage ?? 0}
                                onChange={(e) => handleDiscountChange(user.id, e.target.value)}
                                disabled={updatingId === user.id}
                                className="px-2 py-1.5 bg-[#0D1117] border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-[#00E87A]"
                                title="Partner Discount"
                                data-testid={`discount-select-${user.id}`}
                              >
                                <option value={0}>0%</option>
                                <option value={5}>5%</option>
                                <option value={10}>10%</option>
                                <option value={15}>15%</option>
                                <option value={25}>25%</option>
                                <option value={50}>50%</option>
                              </select>
                            )}

                            {/* Delete User */}
                            {!isCurrentUser && (
                              <button
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                disabled={updatingId === user.id}
                                className="px-2 py-1.5 rounded text-sm text-red-500/60 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                title="Delete user"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Panel — different content for primary vs secondary */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-4 pb-4 pt-0 bg-[#161B22]">
                            {isSecondary ? (
                              <div className="bg-[#0D1117] border border-gray-800 rounded-lg p-5 mt-1" data-testid="secondary-info-panel">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                                    <Link2 className="w-5 h-5 text-orange-400" />
                                  </div>
                                  <div>
                                    <p className="text-white text-sm">
                                      Secondary user of <strong>{primaryInfoMap[user.id]?.name || 'Unknown'}</strong>{' '}
                                      <span className="text-gray-400">({primaryInfoMap[user.id]?.email || ''})</span>
                                    </p>
                                    {primaryInfoMap[user.id]?.id && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setExpandedUserId(primaryInfoMap[user.id].id); }}
                                        className="text-[#00E87A] text-xs hover:underline mt-1 flex items-center gap-1"
                                        data-testid={`go-to-primary-${user.id}`}
                                      >
                                        <ArrowUpCircle className="w-3 h-3" /> View primary user
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <SecondaryUsersPanel
                                userId={user.id}
                                userName={user.full_name || user.email}
                                token={token}
                                onRefreshParent={fetchUsers}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminUsers;
